import { gql } from 'apollo-boost';
import { getPictureUrl } from 'core/api/pictures';
import { useDeviceInfo } from 'core/components/DeviceInfoContext';
import { useLinks } from 'core/components/linksContext';
import { button, buttonAccent } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonthYearDot } from 'core/utils/date';
import { scrollToTop } from 'core/utils/html';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Get52weeksDocument,
    Get52weeksQuery,
    Get52weeksQueryVariables,
    useGet52WeeksSuggestedProductsQuery,
    useReset52weeksMutation,
} from 'site/graphql';
import { getWeekInvestments } from './helpers';
import Service52WeeksInfo from './Service52WeeksInfo';
import Service52WeeksRules from './Service52WeeksRules';
import Service52WeeksTable from './Service52WeeksTable';

gql`
    mutation Reset52weeks {
        reset52weeks
    }
`;

gql`
    query Get52WeeksSuggestedProducts($id1: Int!, $id2: Int!) {
        suggestedProduct1: product(id: $id1) {
            id
            pictureId
            name
        }
        suggestedProduct2: product(id: $id2) {
            id
            pictureId
            name
        }
    }
`;

type IService52WeeksGameProps = {
    data: Get52weeksQuery['get52weeks'];
} & WithStyles<typeof styles>;

const Service52WeeksGame: React.FC<IService52WeeksGameProps> = ({ classes, data }) => {
    const weekInvestments = getWeekInvestments(data);

    const { isMobile } = useDeviceInfo();
    const links = useLinks();
    const [stage, setStage] = useState<'rules' | 'game'>('game');
    const goToRules = () => setStage('rules');
    const goToGame = () => {
        setStage('game');
        scrollToTop();
    };

    const { data: spData } = useGet52WeeksSuggestedProductsQuery({ variables: { id1: 11732, id2: 11807 } });
    const suggestedProduct1 = spData?.suggestedProduct1;
    const suggestedProduct2 = spData?.suggestedProduct2;

    const [reset, { client }] = useReset52weeksMutation({
        onCompleted: () =>
            client.writeQuery<Get52weeksQuery, Get52weeksQueryVariables>({
                query: Get52weeksDocument,
                data: {
                    get52weeks: null,
                },
            }),
    });
    const handleReset = () =>
        confirm('Вы действительно хотите сбросить все данные в сервисе "52 недели богатства"?') && reset();

    if (stage === 'rules') {
        return <Service52WeeksRules label="Назад" onClick={goToGame} />;
    }

    return (
        <>
            <div className={classes.header}>
                <div className={classes.headerInfo}>
                    <p>Дата начала: {formatToLocalDayMonthYearDot(data.startDate)}</p>
                    {data.target && <p>Цель: {data.target}</p>}
                </div>
                <div>
                    <button className={classes.rulesButton} onClick={goToRules}>
                        Правила
                    </button>
                    {!isMobile && (
                        <button className={classes.resetButton} onClick={handleReset}>
                            Сбросить все
                        </button>
                    )}
                </div>
            </div>
            <Service52WeeksInfo weekInvestments={weekInvestments} currency={data.currency} />
            <Service52WeeksTable weekInvestments={weekInvestments} />

            {isMobile && (
                <button className={classes.resetButton} onClick={handleReset}>
                    Сбросить все
                </button>
            )}

            {suggestedProduct1 && suggestedProduct2 && (
                <>
                    <h2 className={classes.suggestedProductsTitle}>Рекомендуемые курсы</h2>
                    <div className={classes.suggestedProducts}>
                        <Link className={classes.productCard} to={links.productDetail.url(suggestedProduct1.id)}>
                            <img
                                src={getPictureUrl(suggestedProduct1.pictureId, '300x300', null)}
                                alt={`Превью курса ${suggestedProduct1.name}`}
                            />
                            <span>{suggestedProduct1.name}</span>
                        </Link>
                        <Link className={classes.productCard} to={links.productDetail.url(suggestedProduct2.id)}>
                            <img
                                src={getPictureUrl(suggestedProduct2.pictureId, '300x300', null)}
                                alt={`Превью курса ${suggestedProduct2.name}`}
                            />
                            <span>{suggestedProduct2.name}</span>
                        </Link>
                    </div>
                </>
            )}
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 50,

            [theme.breakpoints.down('xs')]: {
                flexDirection: 'column',
                marginBottom: 25,
            },
        },
        headerInfo: {
            '& > p': {
                margin: 0,
                fontSize: 16,
                fontWeight: 'bold',
                color: '#808080',
                display: 'inline-block',
            },

            '& > p:first-child': {
                marginRight: 30,

                [theme.breakpoints.down('xs')]: {
                    marginRight: 0,
                },
            },
        },
        resetButton: {
            ...button(theme),
            ...buttonAccent(theme),
            width: 200,
            padding: '10px 16px',
            marginLeft: 20,

            [theme.breakpoints.down('xs')]: {
                marginLeft: 0,
                marginTop: 24,
                width: 100 + '%',
                padding: 16,
                fontSize: 0.9 + 'rem',
            },
        },
        rulesButton: {
            ...button(theme),
            border: '1px solid #DADADA',
            boxSizing: 'border-box',
            width: 200,
            padding: '9px 16px',

            [theme.breakpoints.down('xs')]: {
                marginTop: 10,
                width: 100 + '%',
                padding: 16,
                fontSize: 0.9 + 'rem',
            },
        },
        suggestedProductsTitle: {
            textAlign: 'center',
            marginTop: 4 + 'rem',
            marginBottom: 3 + 'rem',
        },
        suggestedProducts: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 5 + 'rem',

            [theme.breakpoints.down('xs')]: {
                flexDirection: 'column',

                '& > $productCard': {
                    margin: 0,
                },

                '& > $productCard + $productCard': {
                    marginTop: 30,
                },
            },
        },
        productCard: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            width: 250,
            height: 250,
            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.15)',
            margin: '0 50px',

            '&:hover': {
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.3)',
            },

            '& > img': {
                width: 100 + '%',
            },

            '& > span': {
                color: theme.colors.textBase,
                textTransform: 'uppercase',
                fontSize: 14,
                fontWeight: 'bold',
                textAlign: 'center',
            },
        },
    });

export default injectSheet(styles)(Service52WeeksGame);
